// URL des médias stockés (+ variantes d'images allégées, économie de données).
import { onCellular, phoneSaveData } from "@/lib/netStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

let MEDIA_CDN_BASE = "";
try { MEDIA_CDN_BASE = localStorage.getItem("yalola.mediaCdn") || ""; } catch { /* noop */ }
/** Point stored media at the CDN edge directly (skips the /api/files 307 origin round-trip). */
export const setMediaCdnBase = (base) => {
  MEDIA_CDN_BASE = (base || "").replace(/\/+$/, "");
  try { if (MEDIA_CDN_BASE) localStorage.setItem("yalola.mediaCdn", MEDIA_CDN_BASE); } catch { /* noop */ }
};

/** Turn `/api/files/...` (as returned by /upload) into a fully-qualified URL. */
export const resolveMediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("blob:")) return u;
  // Serve stored files straight from the CDN edge — avoids a transatlantic 307
  // redirect through the US origin. Matches BOTH relative "/api/files/..." and
  // already-absolute "https://<origin>/api/files/..." URLs.
  if (MEDIA_CDN_BASE) {
    const idx = u.indexOf("/api/files/");
    if (idx >= 0) return `${MEDIA_CDN_BASE}/${u.slice(idx + "/api/files/".length)}`;
  }
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return BACKEND_URL + u;
  return u;
};

// Variantes générées à l'envoi par le serveur (routes_uploads.build_image_variants) : une photo
// `…-rs.webp` existe aussi en `…-rs_w128.webp`, `_w480`, `_w1280` (plus grand côté). Les anciennes
// photos (sans marqueur `-rs`) restent servies telles quelles — sauf si l'admin a généré leurs variantes
// (Admin › Intégrations › « Anciennes photos ») : settings.image_variants_legacy → `…/X.webp|jpg|png` → `…/X_w480.webp`.
// Lot 679 — tailles 864 / 1080 ajoutées côté serveur (photo seule du fil sur téléphone : 2 à 3× moins de pixels
// décodés que 1280). Elles ne sont demandées qu'une fois publiées dans settings.image_variant_widths (après la
// génération admin pour TOUTES les photos existantes) — avant, la liste reste 128/480/1280 : jamais de 404.
export const IMAGE_VARIANT_WIDTHS = [128, 480, 1280];
const WIDTHS_KEY = "yalola.imgWidths";
let VARIANT_WIDTHS = (() => {
  try { const v = JSON.parse(localStorage.getItem(WIDTHS_KEY) || "null"); return Array.isArray(v) && v.length ? v : IMAGE_VARIANT_WIDTHS; } catch { return IMAGE_VARIANT_WIDTHS; }
})();
/** Tailles annoncées par /config/public (settings.image_variant_widths) : appliquées tout de suite, mémorisées pour le prochain démarrage. */
export const setImageVariantWidths = (list) => {
  const ok = Array.isArray(list) ? list.filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b) : [];
  VARIANT_WIDTHS = ok.length ? ok : IMAGE_VARIANT_WIDTHS;
  try { localStorage.setItem(WIDTHS_KEY, JSON.stringify(VARIANT_WIDTHS)); } catch { /* noop */ }
};
export const imageVariantWidths = () => VARIANT_WIDTHS;
const VARIANT_MARK_RX = /-rs\.webp(?=$|[?#])/;
const LEGACY_RX = /(\/uploads\/[^?#]*?)\.(?:webp|jpe?g|png)(?=$|[?#])/;
const ALREADY_VARIANT_RX = /_w\d+\.webp(?=$|[?#])/;
// Densité plafonnée à 2× : au-delà, le gain visuel sur une vignette est nul mais le poids triple.
const MAX_DPR = 2;

let LEGACY_VARIANTS = false;
try { LEGACY_VARIANTS = localStorage.getItem("yalola.imgLegacyVariants") === "1"; } catch { /* noop */ }
/** Anciennes photos : variantes disponibles (réglage admin, reçu via /config/public). */
export const setLegacyVariants = (on) => {
  LEGACY_VARIANTS = !!on;
  try { localStorage.setItem("yalola.imgLegacyVariants", on ? "1" : "0"); } catch { /* noop */ }
};

// « Économiser mes données » (réglage du membre, par appareil) : off | cellular (données mobiles seulement) | always.
export const DATA_SAVER_MODES = ["off", "cellular", "always"];
let DATA_SAVER = "off";
try { DATA_SAVER = DATA_SAVER_MODES.includes(localStorage.getItem("yalola.dataSaver")) ? localStorage.getItem("yalola.dataSaver") : "off"; } catch { /* noop */ }
export const dataSaverMode = () => DATA_SAVER;
export const setDataSaverMode = (mode) => {
  DATA_SAVER = DATA_SAVER_MODES.includes(mode) ? mode : "off";
  try { localStorage.setItem("yalola.dataSaver", DATA_SAVER); } catch { /* noop */ }
};
/** Actif : « Toujours », ou « Données mobiles » sur un lien cellulaire, ou l'économiseur du téléphone lui-même. */
export const dataSaverActive = () =>
  DATA_SAVER === "always" || (DATA_SAVER === "cellular" && onCellular()) || phoneSaveData();

// Filet de sécurité : une variante jamais générée (photo envoyée après le backfill « Anciennes photos » sans conversion
// serveur) renvoie 404 → l'<img> bascule sur l'original et la photo est mémorisée « sans variantes » (plus de 404 ensuite).
const NO_VARIANTS = new Set();
const VARIANT_ORIGIN = new Map(); // URL de variante → URL de l'original
const rememberVariant = (variant, original) => {
  if (VARIANT_ORIGIN.size > 4000) VARIANT_ORIGIN.clear();
  VARIANT_ORIGIN.set(variant, original);
};
/** <img> dont le chargement a échoué : si c'était une variante connue → original. Renvoie true si basculé. */
export const imageVariantFallback = (el) => {
  if (!el || el.tagName !== "IMG") return false;
  const attr = el.getAttribute("src") || "";
  const original = VARIANT_ORIGIN.get(attr) || VARIANT_ORIGIN.get(el.currentSrc || el.src || "");
  if (!original || original === attr) return false;
  NO_VARIANTS.add(original);
  el.src = original;
  return true;
};
// `error` ne remonte pas depuis <img> → écouteur en phase de capture, une fois pour toute l'app.
if (typeof document !== "undefined") {
  document.addEventListener("error", (e) => { imageVariantFallback(e.target); }, true);
}

/** URL de la variante la plus légère qui reste nette pour la zone affichée (sinon l'original).
 *  `cssWidth` = largeur CSS du cadre ; `cssHeight` (lot 679) = sa hauteur quand elle peut dépasser la largeur (photo
 *  portrait du fil) : les variantes étant nommées par leur PLUS GRAND côté, c'est le plus grand côté affiché qui compte —
 *  sans lui, une photo portrait 390 × 520 px recevrait une variante de 780 px pour 1040 nécessaires (floue).
 *  `widths` = liste à utiliser à la place des tailles publiées (stories : 480/1280 fixes, voir storyPrefetch). */
export const imageVariant = (url, cssWidth, { cssHeight = 0, widths } = {}) => {
  const u = resolveMediaUrl(url);
  if (!u || !(cssWidth > 0) || NO_VARIANTS.has(u)) return u;
  const marked = VARIANT_MARK_RX.test(u);
  if (!marked && !(LEGACY_VARIANTS && LEGACY_RX.test(u) && !ALREADY_VARIANT_RX.test(u))) return u;
  // Économie de données : densité 1× (une photo pleine largeur passe de 1280 à 480 px sur téléphone).
  const dpr = dataSaverActive() ? 1 : (typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, MAX_DPR) : 1);
  const need = Math.max(cssWidth, cssHeight > 0 ? cssHeight : 0) * dpr;
  const w = (widths || VARIANT_WIDTHS).find((x) => x >= need);
  if (!w) return u;
  const v = marked ? u.replace(VARIANT_MARK_RX, `-rs_w${w}.webp`) : u.replace(LEGACY_RX, `$1_w${w}.webp`);
  rememberVariant(v, u);
  return v;
};
